import { useState } from 'react';
import { Box, Input, Select, Option, Typography, Stack, Button } from '@mui/joy';
import { Search } from '@mui/icons-material';
import { useSearchIngredientsQuery } from '@/features/ingredients/api';
import { useDebounce } from '@/hooks/useDebounce';
import { ALL_UNITS_OPTIONS } from '@/features/units/schema';

export function IngredientPickerList({ onAdd, isSaving }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const debouncedSearchTerm = useDebounce(searchQuery, 200);

  const { data, isLoading } = useSearchIngredientsQuery({ name: debouncedSearchTerm, limit: 50 });
  const ingredients = data?.data || [];

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Input
        placeholder="Search ingredients..."
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); setExpandedId(null); }}
        startDecorator={<Search />}
        sx={{ mb: 2, flexShrink: 0 }}
        autoFocus
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {isLoading && <Typography level="body-sm">Loading ingredients...</Typography>}

        {!isLoading && ingredients.length === 0 && (
          <Typography level="body-sm" sx={{ color: 'var(--joy-palette-neutral-400)' }}>
            No ingredients found
          </Typography>
        )}

        <Stack gap={1}>
          {ingredients.map((ingredient) => (
            <IngredientRow
              key={ingredient.id}
              ingredient={ingredient}
              isExpanded={expandedId === ingredient.id}
              isSaving={isSaving}
              onExpand={() => setExpandedId(ingredient.id)}
              onCollapse={() => setExpandedId(null)}
              onAdd={onAdd}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function IngredientRow({ ingredient, isExpanded, isSaving, onExpand, onCollapse, onAdd }) {
  const [amount, setAmount] = useState(1);
  const [unit, setUnit] = useState('g');

  function handleAdd() {
    onAdd({ ingredient_id: ingredient.id, amount: Number(amount), unit });
  }

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 'sm',
        bgcolor: isExpanded ? 'background.level1' : undefined,
        '&:hover': { bgcolor: 'background.level1' },
      }}
    >
      <Box
        onClick={isExpanded ? onCollapse : onExpand}
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        <Typography level="body-md">{ingredient.name}</Typography>
      </Box>

      {isExpanded && (
        <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 1 }}>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            slotProps={{ input: { min: 0, step: '.01' } }}
            sx={{ width: 100 }}
          />
          <Select value={unit} onChange={(e, value) => setUnit(value)} sx={{ minWidth: 110 }}>
            {ALL_UNITS_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Button size="sm" loading={isSaving} onClick={handleAdd}>
            Add
          </Button>
        </Stack>
      )}
    </Box>
  );
}
